/**
 * agentMovementService.ts
 *
 * Frontend bindings for the server-side AI-agent movement loop.
 *
 * Mounts under `/api/v2/agents/...` (see server/v2/routes/agents.js
 * → "Movement loop (slice 1c)").
 *
 * Two stages of life:
 *   1. AUTH    — operator gets a session token by signing a SEP-10
 *                style challenge (G operators) or by going through
 *                the on-chain ownership probe (C operators).
 *   2. CONTROL — every start / pause / resume / stop call sends the
 *                token in `Authorization: Bearer …`.
 *
 * Token lives in `sessionStorage` keyed by `(operator, agent)` so a
 * tab refresh keeps the loop controllable without re-prompting for
 * a wallet signature.
 */

import { Networks } from '@stellar/stellar-sdk';
import type { SigningWallet, PasskeySigner } from './profileService';

// ── Types ──────────────────────────────────────────────────────────────────

export type MovementStatus = 'stopped' | 'running' | 'paused';
export type MovementMode   = 'predefined' | 'random' | 'both';

export interface MovementWaypoint {
  name:      string | null;
  lat:       number;
  lng:       number;
  country:   string | null;
  dwell_s?:  number | null;
  narration?: string | null;
  source?:   string;
}

export interface MovementNearbyAgent {
  agent_pk:     string;
  /** GeoLink ≥ migration 028 (2026-05-09): 'classic' for G-addresses,
   *  'smart_account' for C-addresses. Backend back-fills from the
   *  prefix for older deploys, so this is always present. */
  address_kind: 'classic' | 'smart_account' | null;
  displayName:  string | null;
  distance_m:   number | null;
}

export interface MovementGeofenceHit {
  id:    string | null;
  name:  string | null;
  kind:  string | null;
}

export interface MovementNearbySnapshot {
  radius_m:       number | null;
  nearby_agents:  MovementNearbyAgent[];
  geofence_hits:  MovementGeofenceHit[];
}

export interface MovementLastWaypoint {
  name:    string | null;
  lat:     number;
  lng:     number;
  country: string | null;
  source:  string;
  tickId:  number;
  position_write_ok:      boolean;
  position_write_skipped: boolean;
  position_write_reason:  string | null;
  place_source?:          string | null;
  query?:                 string | null;
  /** True when the random picker had to re-use a previously
   *  visited POI because every fresh cache hit was already in the
   *  recent-dedupe ring. The UI shows a small "↻ repeat" badge so
   *  it's clear the cache is shallow without surfacing it as an
   *  error. */
  repeated?:              boolean;
  /** True when the slug-based search came up dry but the GeoLink
   *  lat/lng cache lookup found something near the agent's last
   *  position. The UI shows a small "⊕ nearby" badge so the
   *  operator knows the loop kept moving without an exact slug
   *  hit. */
  nearby_fallback?:       boolean;
}

/**
 * Per-country diagnostics for the random POI picker, surfaced so
 * the operator can tell at a glance whether GeoLink is actually
 * returning cached entries for the agent's country. Only present
 * when the agent's loop has run at least once with a country set
 * (or the place service has been touched for that country by any
 * other agent in this server's lifetime).
 */
export interface MovementRandomDiagnostics {
  country:         string;
  slugAttempts:    number;
  slugHits:        number;
  slugMisses:      number;
  nearbyAttempts:  number;
  nearbyHits:      number;
  lastSlugHitAt:   number | null;
  lastSlugTriedAt: number | null;
  hotSlugs:        string[];
}

export interface MovementStatusResponse {
  agent_pk:        string;
  status:          MovementStatus;
  mode:            MovementMode;
  country:         string | null;
  intervalMs:      number;
  waypointsCount:  number;
  cursor:          number;
  operator:        string | null;
  startedAt:       string | null;
  pausedAt:        string | null;
  lastTickAt:      string | null;
  lastWaypoint:    MovementLastWaypoint | null;
  lastNearby:      MovementNearbySnapshot | null;
  lastError:       { tick: number; reason: string; message: string | null } | null;
  ticks:           number;
  geolinkSkippedReason: string | null;
  randomDiagnostics: MovementRandomDiagnostics | null;
  exists:          boolean;
}

export interface MovementPlan {
  mode?:       MovementMode;
  country?:    string;
  intervalMs?: number;
}

// ── URL resolution ─────────────────────────────────────────────────────────

function backendBaseUrl(): string {
  if (typeof window === 'undefined') return '';
  const isDev =
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1';
  return isDev
    ? `${window.location.protocol}//${window.location.hostname}:8080`
    : '';
}

function url(path: string): string {
  const base = process.env.REACT_APP_BACKEND_URL || backendBaseUrl();
  return `${base}/api/v2/agents${path}`;
}

function resolveNetworkPassphrase(): string {
  return process.env.REACT_APP_STELLAR_NETWORK_PASSPHRASE || Networks.TESTNET;
}

const STELLAR_G = /^G[A-Z2-7]{55}$/;
const STELLAR_C = /^C[A-Z2-7]{55}$/;

// ── Token cache ────────────────────────────────────────────────────────────

interface CachedToken {
  token:     string;
  expiresAt: number; // unix seconds
}

function tokenStorageKey(operator: string, agent: string): string {
  return `geotrust:movement-token:${operator}:${agent}`;
}

function readCachedToken(operator: string, agent: string): CachedToken | null {
  if (typeof sessionStorage === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(tokenStorageKey(operator, agent));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedToken;
    if (!parsed || typeof parsed.token !== 'string' || !Number.isFinite(parsed.expiresAt)) return null;
    if (parsed.expiresAt * 1000 < Date.now() + 30_000) return null; // expired or near-expired
    return parsed;
  } catch {
    return null;
  }
}

function writeCachedToken(operator: string, agent: string, token: CachedToken): void {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.setItem(tokenStorageKey(operator, agent), JSON.stringify(token));
  } catch { /* quota exceeded — non-fatal */ }
}

function clearCachedToken(operator: string, agent: string): void {
  if (typeof sessionStorage === 'undefined') return;
  try { sessionStorage.removeItem(tokenStorageKey(operator, agent)); } catch { /* ignore */ }
}

// ── Fetch helpers ──────────────────────────────────────────────────────────

async function asJson<T>(resp: Response): Promise<T> {
  if (!resp.ok) {
    const txt = await resp.text().catch(() => resp.statusText);
    let msg = txt;
    try {
      const parsed = JSON.parse(txt);
      msg = parsed.error || parsed.reason || parsed.message || txt;
    } catch { /* keep raw */ }
    throw new Error(msg || `HTTP ${resp.status}`);
  }
  return (await resp.json()) as T;
}

/**
 * Send an authenticated request to a movement-control endpoint.
 *
 * On a 401 we transparently:
 *   1. Clear the cached token (it was either signed with a stale
 *      backend secret, points at an expired exp our 30-second
 *      grace missed, or names a (operator, agent) pair the
 *      backend can no longer prove).
 *   2. Call `ensureMovementSession` again to re-mint a fresh
 *      token. For G operators this re-runs the SEP-10 challenge
 *      (one Freighter pop-up); for C operators it just re-asks
 *      the backend for a new bearer.
 *   3. Retry the original request exactly once with the new
 *      token.
 *
 * The retry is opt-in via the `signers` parameter — callers that
 * don't have a `SigningWallet` / `PasskeySigner` on hand (mostly
 * tests + the public read endpoints) just propagate the 401 to
 * `asJson` like before.
 */
async function authedFetch(
  operator: string,
  agent: string,
  path: string,
  init: RequestInit = {},
  signers?: { stellarWallet?: SigningWallet | null; passkey?: PasskeySigner | null },
): Promise<Response> {
  const cached = readCachedToken(operator, agent);
  if (!cached) {
    throw new Error('no_movement_session_token');
  }
  const send = (token: string): Promise<Response> => {
    const headers = new Headers(init.headers || {});
    headers.set('Authorization', `Bearer ${token}`);
    if (init.body && !headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json');
    }
    return fetch(url(path), { ...init, headers });
  };
  const first = await send(cached.token);
  if (first.status !== 401 || !signers) return first;

  // 401 → re-mint once. If we can't get a fresh token (e.g., the
  // user dismissed the wallet pop-up or the backend still rejects
  // the C-operator), let the original 401 propagate so the panel
  // surfaces the underlying reason.
  clearCachedToken(operator, agent);
  let fresh: { token: string; expiresAt: number } | null = null;
  try {
    fresh = await ensureMovementSession({
      operator,
      agent,
      stellarWallet: signers.stellarWallet || null,
      passkey:       signers.passkey || null,
    });
  } catch { return first; }
  if (!fresh) return first;
  return await send(fresh.token);
}

// ── Auth: prove operator + mint session token ──────────────────────────────

interface ChallengeResponse {
  success: true;
  kind:    'sep10' | 'passkey_v1';
  challengeId?:       string;
  transactionXdr?:    string;
  networkPassphrase?: string;
  expiresAt?:         string;
}

async function getChallenge(operator: string): Promise<ChallengeResponse> {
  const params = new URLSearchParams({ operator });
  const resp = await fetch(url(`/movement/auth/challenge?${params.toString()}`));
  return await asJson<ChallengeResponse>(resp);
}

async function mintToken(args: {
  operator:    string;
  agent:       string;
  challengeId?: string;
  signedXdr?:   string;
  ttlSeconds?:  number;
}): Promise<{ token: string; expiresAt: number }> {
  const resp = await fetch(url('/movement/auth/prove'), {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify(args),
  });
  return await asJson<{ token: string; expiresAt: number }>(resp);
}

/**
 * Ensure we have a session token for (operator, agent). Returns the
 * cached token if still valid; otherwise drives the wallet signature
 * flow (Freighter for G, no-sig fast-path for C in v1) and caches
 * the freshly minted token in sessionStorage.
 */
export async function ensureMovementSession(args: {
  operator:        string;
  agent:           string;
  stellarWallet?:  SigningWallet | null;
  passkey?:        PasskeySigner | null;
}): Promise<{ token: string; expiresAt: number; freshlySigned: boolean }> {
  const { operator, agent, stellarWallet, passkey } = args;
  if (!operator || !agent) throw new Error('operator and agent are required');
  const cached = readCachedToken(operator, agent);
  if (cached) return { token: cached.token, expiresAt: cached.expiresAt, freshlySigned: false };

  if (STELLAR_G.test(operator)) {
    if (!stellarWallet) throw new Error('Connect Freighter (or another G… wallet) to control movement.');
    const ch = await getChallenge(operator);
    if (ch.kind !== 'sep10' || !ch.transactionXdr || !ch.challengeId) {
      throw new Error('Backend returned an invalid challenge for a G operator.');
    }
    const signedXdr = await stellarWallet.signTransaction(
      ch.transactionXdr,
      ch.networkPassphrase || resolveNetworkPassphrase(),
    );
    const minted = await mintToken({
      operator,
      agent,
      challengeId: ch.challengeId,
      signedXdr,
    });
    writeCachedToken(operator, agent, minted);
    return { ...minted, freshlySigned: true };
  }

  if (STELLAR_C.test(operator)) {
    // v1 passkey path: the backend cross-checks operator against
    // the agent's on-chain `operator` field but does not require a
    // signed challenge. This will tighten in v1.1; the
    // PasskeySigner is plumbed through here so we can switch to a
    // passkey-signed Soroban no-op without touching callers.
    void passkey;
    const minted = await mintToken({ operator, agent });
    writeCachedToken(operator, agent, minted);
    return { ...minted, freshlySigned: true };
  }

  throw new Error('Operator must be a Stellar G… or C… address.');
}

export function clearMovementSession(operator: string, agent: string): void {
  clearCachedToken(operator, agent);
}

// ── Read endpoints (public) ────────────────────────────────────────────────

export async function getMovementStatus(agent: string): Promise<MovementStatusResponse> {
  const resp = await fetch(url(`/${encodeURIComponent(agent)}/movement/status`));
  const data = await asJson<{ success: true; status: MovementStatusResponse }>(resp);
  return data.status;
}

export async function getMovementWaypoints(agent: string): Promise<MovementWaypoint[]> {
  const resp = await fetch(url(`/${encodeURIComponent(agent)}/movement/waypoints`));
  const data = await asJson<{ success: true; waypoints: MovementWaypoint[] }>(resp);
  return data.waypoints || [];
}

/**
 * ISO-2 countries that currently have at least one running/paused
 * agent loop, across every operator. Public (no wallet) — the map
 * uses this to keep a marker subscription open for every country with
 * a live agent, so a teleported agent (e.g. in Portugal) paints for
 * every viewer regardless of camera position or wallet state.
 */
export async function getActiveAgentCountries(): Promise<string[]> {
  const resp = await fetch(url('/movement/active-countries'));
  const data = await asJson<{ success: true; countries: string[] }>(resp);
  return Array.isArray(data.countries) ? data.countries : [];
}

// ── Write endpoints (operator-token) ───────────────────────────────────────

// Optional bag of wallet signers. Pass on every write so the
// authedFetch 401-retry can re-mint a session without forcing
// the caller to react to "no_movement_session_token" themselves.
export interface MovementWriteSigners {
  stellarWallet?: SigningWallet | null;
  passkey?:       PasskeySigner  | null;
}

export async function setMovementWaypoints(
  operator: string,
  agent: string,
  waypoints: MovementWaypoint[],
  signers?: MovementWriteSigners,
): Promise<MovementStatusResponse> {
  const resp = await authedFetch(
    operator, agent,
    `/${encodeURIComponent(agent)}/movement/waypoints`,
    { method: 'PUT', body: JSON.stringify({ waypoints }) },
    signers,
  );
  const data = await asJson<{ success: true; status: MovementStatusResponse }>(resp);
  return data.status;
}

export async function startMovement(
  operator: string,
  agent: string,
  plan: MovementPlan,
  signers?: MovementWriteSigners,
): Promise<MovementStatusResponse> {
  const resp = await authedFetch(
    operator, agent,
    `/${encodeURIComponent(agent)}/movement/start`,
    { method: 'POST', body: JSON.stringify({ plan }) },
    signers,
  );
  const data = await asJson<{ success: true; status: MovementStatusResponse }>(resp);
  return data.status;
}

export async function pauseMovement(
  operator: string,
  agent: string,
  signers?: MovementWriteSigners,
): Promise<MovementStatusResponse> {
  const resp = await authedFetch(
    operator, agent,
    `/${encodeURIComponent(agent)}/movement/pause`,
    { method: 'POST' },
    signers,
  );
  const data = await asJson<{ success: true; status: MovementStatusResponse }>(resp);
  return data.status;
}

export async function resumeMovement(
  operator: string,
  agent: string,
  signers?: MovementWriteSigners,
): Promise<MovementStatusResponse> {
  const resp = await authedFetch(
    operator, agent,
    `/${encodeURIComponent(agent)}/movement/resume`,
    { method: 'POST' },
    signers,
  );
  const data = await asJson<{ success: true; status: MovementStatusResponse }>(resp);
  return data.status;
}

export async function stopMovement(
  operator: string,
  agent: string,
  signers?: MovementWriteSigners,
): Promise<MovementStatusResponse> {
  const resp = await authedFetch(
    operator, agent,
    `/${encodeURIComponent(agent)}/movement/stop`,
    { method: 'POST' },
    signers,
  );
  const data = await asJson<{ success: true; status: MovementStatusResponse }>(resp);
  return data.status;
}

export interface TeleportTarget {
  lat:     number;
  lng:     number;
  country: string;
  name?:   string | null;
}

/**
 * One-shot operator-driven teleport — moves the agent to a single
 * point without starting / mutating the recurring movement loop.
 * Server-side reuses the same position-write path as the loop and
 * broadcasts the resulting frame so the marker hops immediately on
 * every connected browser.
 */
export async function teleportAgent(
  operator: string,
  agent: string,
  target: TeleportTarget,
  signers?: MovementWriteSigners,
): Promise<MovementStatusResponse> {
  const resp = await authedFetch(
    operator, agent,
    `/${encodeURIComponent(agent)}/movement/teleport`,
    {
      method: 'POST',
      body:   JSON.stringify({
        lat:     Number(target.lat),
        lng:     Number(target.lng),
        country: String(target.country || '').toUpperCase(),
        name:    target.name || null,
      }),
    },
    signers,
  );
  const data = await asJson<{ success: true; status: MovementStatusResponse }>(resp);
  return data.status;
}
